import { useState, useEffect } from 'react';
import { Server, Copy, Check, ShieldAlert } from 'lucide-react';
import Modal from '../common/Modal';
import { EDGE_API_URL } from '../../utils/edgeApiUrl';

export interface MtgeFormData {
  id?: string;
  device_name: string;
  max_tenants: number;
  base_wg_port: number;
  wan_ip: string;
  wan_cidr: string;
  wan_gateway: string;
  firmware_version: string;
}

export interface MtgeCreationResult {
  id: string;
  serialNumber: string;
  deviceName: string;
  status: string;
  deploymentPassword: string;
  provisionToken?: string;
}

interface MTGEModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: MtgeFormData) => any | Promise<any>;
  mtge?: MtgeFormData;
  mode: 'add' | 'edit';
}

const defaultFormData: MtgeFormData = {
  device_name: '',
  max_tenants: 10,
  base_wg_port: 51820,
  wan_ip: '',
  wan_cidr: '/24',
  wan_gateway: '',
  firmware_version: '',
};

const CopyableField = ({ label, value, mono, highlight }: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <button
          onClick={handleCopy}
          className="inline-flex items-center space-x-1 text-xs text-gray-500 hover:text-primary-600 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <p className={`text-sm ${highlight ? 'text-green-800 font-semibold' : 'text-gray-900'} ${mono ? 'font-mono' : ''} break-all`}>
        {value}
      </p>
    </div>
  );
};

const MTGEModal = ({ isOpen, onClose, onSave, mtge, mode }: MTGEModalProps) => {
  const [formData, setFormData] = useState<MtgeFormData>(mtge || defaultFormData);
  const [saving, setSaving] = useState(false);
  const [creationResult, setCreationResult] = useState<MtgeCreationResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData(mtge || defaultFormData);
      setCreationResult(null);
    }
  }, [isOpen, mtge]);

  const handleChange = (field: keyof MtgeFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const result = await onSave(formData);
      if (mode === 'add' && result && 'serialNumber' in result) {
        setCreationResult(result);
      } else {
        onClose();
      }
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  // Credentials view after successful creation
  if (creationResult) {
    const credentialsFooter = (
      <div className="flex items-center justify-end">
        <button onClick={onClose} className="btn-primary">Done</button>
      </div>
    );

    const token = creationResult.provisionToken || creationResult.deploymentPassword;
    const installerCmd = `sudo securelink-installer install \\\n  --token ${token} \\\n  --url ${EDGE_API_URL} \\\n  --management-interface ens7`;

    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Shared Gateway Created" size="lg" footer={credentialsFooter}>
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Save these credentials now</p>
                <p className="text-sm text-amber-700 mt-1">
                  The deployment token will only be shown once and cannot be retrieved later.
                </p>
              </div>
            </div>
          </div>

          <CopyableField label="Device Name" value={creationResult.deviceName} />
          <CopyableField label="Serial Number (GUID)" value={creationResult.serialNumber} mono />
          {creationResult.provisionToken && (
            <CopyableField label="Deployment Token" value={creationResult.provisionToken} mono highlight />
          )}

          <div className="border-t pt-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Installer Command</p>
            <p className="text-xs text-gray-500 mb-2">
              Run this on the target device (use <code className="bg-gray-100 px-1 rounded">nohup</code> if SSH is on the WAN interface):
            </p>
            <CopyableField label="" value={installerCmd} mono />
          </div>
        </div>
      </Modal>
    );
  }

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">Cancel</button>
      <button
        onClick={handleSubmit}
        disabled={saving || !formData.device_name}
        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
      >
        <Server className="w-4 h-4" />
        <span>{saving ? 'Saving...' : mode === 'add' ? 'Create MTGE' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Register New Shared Gateway' : `Edit: ${mtge?.device_name}`}
      size="lg"
      footer={footer}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.device_name}
              onChange={(e) => handleChange('device_name', e.target.value)}
              placeholder="e.g., MTGE-SYD-01"
              className="input-base w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WAN IP</label>
            <input
              type="text"
              value={formData.wan_ip}
              onChange={(e) => handleChange('wan_ip', e.target.value)}
              placeholder="e.g., 203.0.113.50"
              className="input-base w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WAN Gateway</label>
            <input
              type="text"
              value={formData.wan_gateway}
              onChange={(e) => handleChange('wan_gateway', e.target.value)}
              placeholder="e.g., 203.0.113.1 (auto-derived if empty)"
              className="input-base w-full"
            />
            <p className="mt-1 text-xs text-gray-500">Leave empty to auto-derive from WAN IP (.1)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Firmware Version</label>
            <input
              type="text"
              value={formData.firmware_version}
              onChange={(e) => handleChange('firmware_version', e.target.value)}
              placeholder="e.g., 2.4.5"
              className="input-base w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Tenants</label>
            <input
              type="number"
              value={formData.max_tenants}
              onChange={(e) => handleChange('max_tenants', parseInt(e.target.value) || 10)}
              min={1}
              max={50}
              className="input-base w-full"
            />
            <p className="mt-1 text-xs text-gray-500">Maximum number of tenants (1-50)</p>
          </div>

          {mode === 'add' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Base Tunnel Port</label>
              <input
                type="number"
                value={formData.base_wg_port}
                onChange={(e) => handleChange('base_wg_port', parseInt(e.target.value) || 51820)}
                min={1024}
                max={65535}
                className="input-base w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Starting port for encrypted tunnels (3 ports per tenant)</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MTGEModal;
